import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

/**
 * One aggregate row, used identically for the model, specialist and task-type
 * tables so the three read the same way.
 *
 * Rates are null rather than 0 when there is nothing to divide by. The UI must
 * render null as "—", never as 0%, or an unmeasured model looks like a failing
 * one.
 */
export interface PerformanceRow {
  key: string
  label: string
  runs: number
  resolvedRuns: number
  successfulRuns: number
  firstPassRate: number | null
  finalSuccessRate: number | null
  escalationRate: number | null
  deliveryRate: number | null
  avgAttempts: number | null
  avgLlmCalls: number | null
  usdCost: number
  avgUsdPerRun: number | null
  usdPerSuccess: number | null
  usdPerStableSuccess: number | null
  avgDurationMs: number | null
  avgQualityScore: number | null
  technicalVerifiedRate: number | null
  stableRate: number | null
  credits: number
  tokensIn: number
  tokensOut: number
}

export interface SummaryCards {
  totalRuns: number
  resolvedRuns: number
  successfulRuns: number
  firstPassRate: number | null
  finalSuccessRate: number | null
  escalationRate: number | null
  avgAttempts: number | null
  avgUsdPerRun: number | null
  usdPerSuccess: number | null
  avgDurationMs: number | null
  totalUsdSpend: number
  totalCredits: number
  humanRatedRate: number | null
  unresolvedRuns: number
}

export interface EscalationEdge {
  fromModel: string
  toModel: string
  escalations: number
  rescued: number
  rescueRate: number | null
  usdCost: number
}

export interface Recommendation {
  agentKey: string
  taskType: string | null
  recommendedModel: string | null
  runs: number
  firstPassRate: number | null
  usdPerSuccess: number | null
  runnerUpModel: string | null
  runnerUpFirstPassRate: number | null
  runnerUpUsdPerSuccess: number | null
  rationale: string
}

export interface TrendPoint {
  day: string
  runs: number
  calls: number
  usdCost: number
  backfilledCalls: number
}

/**
 * One rung of the success funnel.
 *
 * `judged` is what keeps this readable. Each layer resolves on a different
 * schedule, so a rung with few judged runs is waiting, not failing — which is
 * why the UI shows the rate over judged runs and the judged count next to it.
 */
export interface FunnelStage {
  key: string
  label: string
  reached: number
  judged: number
  rateOfJudged: number | null
  rateOfAll: number | null
  note: string
}

export interface OutcomeSlice {
  value: string
  runs: number
  share: number | null
}

export interface OutcomeDistributions {
  technical: OutcomeSlice[]
  task: OutcomeSlice[]
  stability: OutcomeSlice[]
}

/** A specialist evidence signal, discovered from run data rather than declared. */
export interface EvidenceSignal {
  agentKey: string
  signal: string
  runs: number
  kind: 'boolean' | 'number' | 'label'
  trueRate: number | null
  avgValue: number | null
}

export interface ModelCatalogRow {
  modelKey: string
  provider: string | null
  label: string | null
  status: string
  capability: string
  usdInPerMtok: number | null
  usdOutPerMtok: number | null
  usdPerUnit: number | null
  pricingUnit: string
  pricingSource: string
  firstSeenAt: string | null
  lastSeenAt: string | null
}

/**
 * A specialized tool, measured on its own terms.
 *
 * None of these fields exist on PerformanceRow, and that is deliberate: a tool
 * that renders what an agent decided did not deliver the work, so asking it for
 * a success rate would compare it against models doing a different job.
 */
export interface VisualToolRow {
  modelKey: string
  provider: string | null
  label: string | null
  agentKey: string
  calls: number
  failedCalls: number
  imagesGenerated: number
  imageToImageCalls: number
  avgIterations: number | null
  avgImagesPerCall: number | null
  avgDurationMs: number | null
  usdCost: number
  avgUsdPerCall: number | null
  avgUsdPerImage: number | null
  selectionRate: number | null
  firstSelectionRate: number | null
  finalUsedRate: number | null
  builderUsedRate: number | null
  designAcceptanceRate: number | null
  usdPerAcceptedDesign: number | null
}

export interface VisualToolSummary {
  calls: number
  imagesGenerated: number
  totalUsdSpend: number
  avgIterations: number | null
  firstSelectionRate: number | null
  finalUsedRate: number | null
  designAcceptanceRate: number | null
  tasksWithVisuals: number
}

export interface DeliveryPerformanceRow {
  key: string
  label: string
  /** Lineup tier from effective agent lineups. */
  tier?: 'primary' | 'delegate' | 'unlisted'
  deliveredRuns: number
  taskJudgedRuns: number
  acceptedRuns: number
  correctedRuns: number
  avgQualityScore: number | null
  acceptedRate: number | null
  primaryDeliveryUsd: number
  usdPerAccepted: number | null
  avgDurationMs: number | null
  technicalSuccessRuns: number
  technicalSuccessRate: number | null
}

export interface ModelPerformanceData {
  filters: {
    dateFrom: string
    dateTo: string
    projectId: number | null
    agentKey: string | null
    taskType: string | null
    modelKey: string | null
    complexity: string | null
  }
  summary: SummaryCards
  byModel: PerformanceRow[]
  byDeliveryModel: DeliveryPerformanceRow[]
  bySpecialist: PerformanceRow[]
  byTaskType: PerformanceRow[]
  escalationMatrix: EscalationEdge[]
  recommendations: Recommendation[]
  trend: TrendPoint[]
  catalog: ModelCatalogRow[]
  funnel: FunnelStage[]
  outcomes: OutcomeDistributions
  evidence: EvidenceSignal[]
  visualTools: VisualToolRow[]
  visualSummary: VisualToolSummary
  caveats: string[]
}

export interface ModelPerformanceFilterOptions {
  projects: Array<{ id: number; name: string }>
  agents: string[]
  taskTypes: string[]
  models: string[]
  complexities: string[]
  dataRange: { firstDate: string | null; lastDate: string | null }
}

export interface ModelPerformanceQueryArgs {
  dateFrom?: string
  dateTo?: string
  projectId?: string
  agentKey?: string
  taskType?: string
  modelKey?: string
  complexity?: string
  /** primary (default) | research — Slice 4 research aggregates */
  axis?: 'primary' | 'research'
}

// --- Agent lineups ---------------------------------------------------------
//
// A lineup is the ordered list of models one specialist may run on, split into
// PRIMARY (may own a task) and DELEGATE (takes sub-tasks from a primary). The
// governance rule these types exist to serve: measured performance may reorder
// a tier, a project may reorder within a tier, and only an admin may move a
// model between tiers.

export type LineupTier = 'primary' | 'delegate'
export type LineupEligibility = 'candidate' | 'backup' | 'experimental' | 'disabled'

export interface LineupEntry {
  modelKey: string
  label: string | null
  provider: string | null
  tier: LineupTier
  priority: number
  eligibility: LineupEligibility
  enabled: boolean
  isDefault: boolean
  /** Placed by the API's premium rule rather than listed in code. */
  premium: boolean
}

export interface AgentLineup {
  collaboratorKey: string
  /** What the API's code ships. Empty until the API has published. */
  shipped: LineupEntry[]
  /** The admin's platform-wide replacement, or empty when none. */
  platformOverride: LineupEntry[]
  /** True when platformOverride is what the router is actually using. */
  customized: boolean
  /** When the API last published its code lineups. Null means never. */
  publishedAt: string | null
}

export interface ProjectLineupOverride {
  projectId: number
  projectName: string | null
  collaboratorKey: string
  entries: LineupEntry[]
}

export interface AgentLineupsResponse {
  lineups: AgentLineup[]
  catalog: Array<{ modelKey: string; label: string; provider: string | null }>
}

/** What the editor sends back. Priority is implied by array order. */
export interface LineupEntryInput {
  modelKey: string
  tier: LineupTier
  eligibility: LineupEligibility
  enabled: boolean
  isDefault: boolean
}

export interface BusinessKnowledgeEvent {
  uuid: string
  projectId: number | null
  projectName: string | null
  op: string
  accepted: boolean | number
  rejectReason: string | null
  actorSource: string | null
  actorUserId: number | null
  summary: string | null
  claimId: number | null
  createdAt: string
}

export interface BusinessKnowledgeEventsResponse {
  events: BusinessKnowledgeEvent[]
  available: boolean
}

export const modelPerformanceApi = createApi({
  reducerPath: 'modelPerformanceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['ModelPerformance', 'ModelCatalog', 'AgentLineups', 'BusinessKnowledgeEvents'],
  endpoints: (builder) => ({
    getModelPerformance: builder.query<{ data: ModelPerformanceData }, ModelPerformanceQueryArgs>({
      query: (params) => ({ url: '/model-performance', params }),
      providesTags: ['ModelPerformance', 'ModelCatalog'],
    }),

    getModelPerformanceFilters: builder.query<{ data: ModelPerformanceFilterOptions }, void>({
      query: () => ({ url: '/model-performance/filters' }),
      providesTags: ['ModelCatalog'],
    }),

    getBusinessKnowledgeEvents: builder.query<
      { data: BusinessKnowledgeEventsResponse },
      { projectId?: string; limit?: number; includeRejected?: boolean } | void
    >({
      query: (args) => ({
        url: '/model-performance/business-knowledge-events',
        params: {
          projectId: args?.projectId,
          limit: args?.limit ?? 50,
          includeRejected: args?.includeRejected === false ? '0' : '1',
        },
      }),
      providesTags: ['BusinessKnowledgeEvents'],
    }),

    updateModelPricing: builder.mutation<
      { data: { modelKey: string } },
      { modelKey: string; usdInPerMtok: number | null; usdOutPerMtok: number | null }
    >({
      query: ({ modelKey, ...body }) => ({
        url: `/model-performance/models/${encodeURIComponent(modelKey)}/pricing`,
        method: 'PATCH',
        body,
      }),
      // Pricing only affects calls recorded after the change, so the analytics
      // numbers do not move — but the catalog's priced/unpriced warning does.
      invalidatesTags: ['ModelCatalog'],
    }),

    getAgentLineups: builder.query<{ data: AgentLineupsResponse }, void>({
      query: () => ({ url: '/model-performance/lineups' }),
      providesTags: ['AgentLineups'],
    }),

    getProjectLineupOverrides: builder.query<
      { data: { overrides: ProjectLineupOverride[] } },
      { collaboratorKey?: string } | void
    >({
      query: (args) => ({
        url: '/model-performance/lineups/projects',
        params: args?.collaboratorKey ? { collaboratorKey: args.collaboratorKey } : undefined,
      }),
      providesTags: ['AgentLineups'],
    }),

    savePlatformLineup: builder.mutation<
      { data: { collaboratorKey: string; entries: LineupEntry[] } },
      { collaboratorKey: string; entries: LineupEntryInput[]; note?: string }
    >({
      query: ({ collaboratorKey, ...body }) => ({
        url: `/model-performance/lineups/${encodeURIComponent(collaboratorKey)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AgentLineups'],
    }),

    resetPlatformLineup: builder.mutation<{ data: { collaboratorKey: string } }, string>({
      query: (collaboratorKey) => ({
        url: `/model-performance/lineups/${encodeURIComponent(collaboratorKey)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AgentLineups'],
    }),

    resetProjectLineup: builder.mutation<
      { data: { projectId: number; collaboratorKey: string } },
      { projectId: number; collaboratorKey: string }
    >({
      query: ({ projectId, collaboratorKey }) => ({
        url: `/model-performance/lineups/projects/${projectId}/${encodeURIComponent(collaboratorKey)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AgentLineups'],
    }),
  }),
})

export const {
  useGetModelPerformanceQuery,
  useGetModelPerformanceFiltersQuery,
  useGetBusinessKnowledgeEventsQuery,
  useUpdateModelPricingMutation,
  useGetAgentLineupsQuery,
  useGetProjectLineupOverridesQuery,
  useSavePlatformLineupMutation,
  useResetPlatformLineupMutation,
  useResetProjectLineupMutation,
} = modelPerformanceApi
